import * as React from 'react';
import {Clear} from '@mui/icons-material';
import {UserFilled} from '@carbon/icons-react';

import {
  Box,
  Stack,
  Typography,
  Button,
  ClickAwayListener,
  Paper,
  Popper,
  Grid,
  IconButton,
  useTheme,
  Tooltip,
  TooltipProps,
  styled,
  tooltipClasses,
  Link,
} from '@mui/material';

import {useSelector, useDispatch} from 'react-redux';
import {selectAllRoles} from 'app/store/operatingModel/operatingModelSlice';
import {
  setSelectedRoleFilters,
  selectRoleFilters,
  selectOnboarding,
  setOnboarding,
  OnboardingActions,
  selectRoleOrganization,
  setRoleOrganization,
} from 'app/store/appState/appStateSlice';

import {CheckboxFilter} from 'components/CheckboxFilter/CheckboxFilter';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import {selectUserType} from 'app/store/users/userSlice';
import {LogInType} from 'app/types/Shared';

const ChangeRole = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {trackEvent} = useMatomo();
  const allRoles = useSelector(selectAllRoles);
  const selectedRoles = useSelector(selectRoleFilters);
  const onboarding = useSelector(selectOnboarding);
  const organization = useSelector(selectRoleOrganization);
  const userType = useSelector(selectUserType);
  const functionalCategoriesByOrg =
    allRoles?.role_categories.filter((rc) => rc.level1 === organization).map((r) => (r.label ? r.label : '')) ?? [];
  const roleOrganizations = allRoles?.role_categories?.reduce((orgs, curr) => {
    if (curr.level1 && !orgs.includes(curr.level1)) orgs.push(curr.level1);
    return orgs;
  }, [] as Array<string>);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRolesState, setSelectedRolesState] = React.useState(selectedRoles);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper1' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onboarding?.roleGuide) {
      dispatch(setOnboarding(OnboardingActions.DisableRoleGuide));
    }
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const SelectRoles = () => {
    // Add matomo tracking here
    const selectedRoleFunctions = allRoles?.role_categories.reduce((acc, curr) => {
      if (curr.label && selectedRolesState.includes(curr.label) && !selectedRoles.includes(curr.label) && curr.level1) {
        if (!acc.includes(curr.level1)) {
          acc.push(curr.level1);
        }
      }
      return acc;
    }, [] as Array<string>);
    //track new Functions Selected
    selectedRoleFunctions?.forEach((area) => {
      trackEvent({category: 'Guide Me', action: 'Function Selected', name: area});
    });
    //track new Roles Selected
    if (selectedRolesState.length > 0) {
      selectedRolesState.forEach((role) => {
        if (!selectedRoles.includes(role)) {
          trackEvent({category: 'Guide Me', action: 'Sub-Function Selected', name: role});
        }
      });
    }
    //const getRolesAndOrganizations = roleOrganizations
    dispatch(setSelectedRoleFilters(selectedRolesState));
  };

  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<null | HTMLElement>(null);

  React.useEffect(() => {
    if (onboarding?.roleGuide && popoverAnchorEl === null) {
      setPopoverAnchorEl(document.getElementById('Guide_Me_Role_Component'));
    }
  }, [onboarding?.roleGuide, popoverAnchorEl]);

  React.useEffect(() => {
    if (userType === LogInType.External && (selectedRoles?.length === 0 || selectedRoles?.length > 1)) {
      const getCRORole = allRoles?.roles.find((role) => role.class_name === 'CRO');
      if (getCRORole?.role_category.label) {
        dispatch(setSelectedRoleFilters([getCRORole?.role_category.label]));
      }
    }
  }, [userType, selectedRoles, dispatch, allRoles]);

  const popoverId = open ? 'simple-popover' : undefined;
  const handleClose = () => {
    setPopoverAnchorEl(null);
    dispatch(setOnboarding(OnboardingActions.DisableRoleGuide));
  };

  const handleDelete = (role: string) => {
    const arr = selectedRolesState.filter((x) => x !== role);
    setSelectedRolesState(arr);
    if (arr.length === 0) {
      dispatch(setSelectedRoleFilters(arr));
    }
  };

  const RoleTooltip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}} />
  ))(({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'white',
      color: 'black',
      boxShadow: `0px 0px 10px 0px rgba(69, 10, 10, 0.5)`,
      fontSize: 16,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: 'white',
      '&::before': {
        boxShadow: 'none',
      },
    },
  }));

  const TwoTonedUserMultiple = () => (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M16.875 16.875H15.75V14.0625C15.7492 13.3168 15.4526 12.602 14.9253 12.0747C14.398 11.5474 13.6832 11.2508 12.9375 11.25V10.125C13.9814 10.1262 14.9823 10.5414 15.7205 11.2795C16.4586 12.0177 16.8738 13.0186 16.875 14.0625V16.875ZM12.375 16.875H11.25V14.0625C11.2491 13.3168 10.9525 12.602 10.4253 12.0747C9.89801 11.5475 9.18315 11.2509 8.4375 11.25H5.0625C4.31685 11.2509 3.60199 11.5475 3.07473 12.0747C2.54748 12.602 2.25088 13.3168 2.25 14.0625V16.875H1.125V14.0625C1.12622 13.0186 1.54146 12.0178 2.27962 11.2796C3.01778 10.5415 4.01858 10.1262 5.0625 10.125H8.4375C9.48141 10.1262 10.4822 10.5415 11.2204 11.2796C11.9585 12.0178 12.3738 13.0186 12.375 14.0625V16.875ZM11.25 1.125V2.25C11.9959 2.25 12.7113 2.54632 13.2387 3.07376C13.7662 3.60121 14.0625 4.31658 14.0625 5.0625C14.0625 5.80842 13.7662 6.52379 13.2387 7.05124C12.7113 7.57868 11.9959 7.875 11.25 7.875V9C12.2943 9 13.2958 8.58516 14.0342 7.84673C14.7727 7.10831 15.1875 6.10679 15.1875 5.0625C15.1875 4.01821 14.7727 3.01669 14.0342 2.27827C13.2958 1.53984 12.2943 1.125 11.25 1.125ZM6.75 2.25C7.30626 2.25 7.85003 2.41495 8.31254 2.72399C8.77505 3.03303 9.13554 3.47229 9.34841 3.9862C9.56128 4.50012 9.61698 5.06562 9.50846 5.61119C9.39994 6.15676 9.13207 6.6579 8.73874 7.05124C8.3454 7.44457 7.84426 7.71244 7.29869 7.82096C6.75312 7.92948 6.18762 7.87378 5.6737 7.66091C5.15979 7.44804 4.72053 7.08755 4.41149 6.62504C4.10245 6.16253 3.9375 5.61876 3.9375 5.0625C3.9375 4.31658 4.23382 3.60121 4.76126 3.07376C5.28871 2.54632 6.00408 2.25 6.75 2.25ZM6.75 1.125C5.97124 1.125 5.20996 1.35593 4.56244 1.78859C3.91492 2.22125 3.41024 2.8362 3.11222 3.55568C2.8142 4.27517 2.73623 5.06687 2.88816 5.83067C3.04009 6.59447 3.4151 7.29606 3.96577 7.84673C4.51644 8.3974 5.21803 8.77241 5.98183 8.92434C6.74563 9.07627 7.53733 8.9983 8.25682 8.70028C8.9763 8.40226 9.59125 7.89758 10.0239 7.25006C10.4566 6.60254 10.6875 5.84126 10.6875 5.0625C10.6875 4.01821 10.2727 3.01669 9.53423 2.27827C8.79581 1.53984 7.79429 1.125 6.75 1.125Z'
        fill='#34373F'
      />
      <path
        d='M1 17H6.75H12.5V14.1429C12.4988 13.0824 12.0743 12.0657 11.3197 11.3158C10.5652 10.5659 9.54211 10.1441 8.475 10.1429H5.025C3.95789 10.1441 2.93484 10.5659 2.18027 11.3158C1.42571 12.0657 1.00125 13.0824 1 14.1429V17ZM2.725 5C2.725 5.79113 2.96106 6.56448 3.40333 7.22228C3.84561 7.88008 4.47423 8.39277 5.2097 8.69552C5.94517 8.99827 6.75447 9.07748 7.53524 8.92314C8.31601 8.7688 9.0332 8.38784 9.59611 7.82843C10.159 7.26902 10.5424 6.55629 10.6977 5.78036C10.853 5.00444 10.7733 4.20017 10.4686 3.46927C10.164 2.73836 9.64808 2.11365 8.98617 1.67412C8.32426 1.2346 7.54607 1 6.75 1C5.6825 1 4.65873 1.42143 3.9039 2.17157C3.14906 2.92172 2.725 3.93913 2.725 5Z'
        fill='#34373F'
      />
    </svg>
  );

  const clickGuideMeLink = () => {
    const guideMe = document.getElementById('Guide_Me_Role_Component');
    guideMe?.click();
  };

  const GuideMe = () => {
    if (selectedRoles.length === 0) {
      return (
        <Stack
          direction='row'
          spacing='5px'
          alignItems='center'
          justifyContent='center'
          sx={
            open
              ? {
                  border: '1px solid',
                  borderColor: theme.palette.primary.main,
                  borderRadius: '4px',
                  background: '#F2F2F2',
                  height: '28px',
                  px: '10px',
                  ':hover': {
                    background: 'white',
                  },
                }
              : {
                  border: '1px solid',
                  borderColor: theme.palette.grey[600],
                  borderRadius: '4px',
                  background: theme.palette.grey[200],
                  height: '28px',
                  px: '10px',
                  ':hover': {
                    borderColor: theme.palette.primary.main,
                    background: 'white',
                  },
                }
          }
        >
          <UserFilled width='22px' height='22px' style={{color: 'grey.800'}} />
          <Typography fontSize='12px' fontWeight={700}>
            Guide Me
          </Typography>
        </Stack>
      );
    } else if (selectedRoles.length === 1) {
      const getStyles = () => {
        if (userType === LogInType.External) {
          return {
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            borderRadius: '4px',
            background: 'white',
            height: '28px',
            px: '10px',
          };
        } else if (open) {
          return {
            border: '1px solid',
            borderColor: theme.palette.grey[500],
            borderRadius: '4px',
            background: theme.palette.grey[500],
            height: '28px',
            px: '10px',
            ':hover': {
              border: '1px solid transparent',
              background: theme.palette.grey[100],
            },
          };
        } else {
          return {
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            borderRadius: '4px',
            background: 'white',
            height: '28px',
            px: '10px',
            ':hover': {
              border: '1px solid transparent',
              background: theme.palette.grey[100],
            },
          };
        }
      };
      return (
        <Stack direction='row' spacing='5px' alignItems='center' justifyContent='center' sx={getStyles()}>
          <UserFilled width='18px' height='18px' style={{color: 'grey.800'}} />
          <Typography fontSize='12px' fontWeight={700}>
            {selectedRoles.join('')}
          </Typography>
        </Stack>
      );
    } else {
      return (
        <RoleTooltip
          arrow
          title={
            <Stack py={0.5} spacing='10px'>
              {selectedRoles.map((role) => (
                <Typography key={role} variant='h6' fontSize='14px'>
                  {role}
                </Typography>
              ))}
            </Stack>
          }
        >
          <Stack
            direction='row'
            spacing='5px'
            alignItems='center'
            justifyContent='center'
            sx={
              open
                ? {
                    border: '1px solid',
                    borderColor: theme.palette.grey[500],
                    borderRadius: '4px',
                    background: theme.palette.grey[500],
                    height: '28px',
                    px: '10px',
                    ':hover': {
                      border: '1px solid transparent',
                      background: theme.palette.grey[100],
                    },
                  }
                : {
                    border: '1px solid',
                    borderColor: theme.palette.primary.main,
                    borderRadius: '4px',
                    background: 'white',
                    height: '28px',
                    px: '10px',
                    ':hover': {
                      border: '1px solid transparent',
                      background: theme.palette.grey[100],
                    },
                  }
            }
          >
            <TwoTonedUserMultiple />
            <Typography fontSize='12px' fontWeight={700}>
              Multiple Selections
            </Typography>
          </Stack>
        </RoleTooltip>
      );
    }
  };

  return (
    <Box width='100%' display='flex' justifyContent='flex-end' alignItems='flex-end' flexGrow={1}>
      {userType === LogInType.Internal ? (
        <React.Fragment>
          <Button
            variant='text'
            size='small'
            onClick={handleClick}
            id='Guide_Me_Role_Component'
            sx={{p: 0, color: 'black', mr: 2}}
            disableRipple
            disableFocusRipple
          >
            <GuideMe />
          </Button>
          <Popper id={id} open={open} anchorEl={anchorEl} placement='bottom-end' sx={{zIndex: 150}}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <Box width='100%' px={2}>
                <Paper
                  sx={{
                    background: theme.palette.grey[200],
                    minWidth: 950,
                    maxWidth: 950,
                    pb: 1,
                    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
                  }}
                >
                  <Stack direction='column' spacing={0.5} width='100%' px={2} pb={1}>
                    <Stack direction='row'>
                      <Box flexGrow={1} pt={2}>
                        <Typography variant='h1'>Make one or more selections</Typography>
                      </Box>
                      <IconButton
                        sx={{p: 0.5}}
                        disableRipple
                        disableFocusRipple
                        onClick={() => {
                          handleClickAway();
                        }}
                      >
                        <Clear />
                      </IconButton>
                    </Stack>
                    <Typography variant='h6'>
                      A profile icon will lead you to relevant content. It will not hide any content or change
                      permissions.
                    </Typography>
                  </Stack>
                  <Grid container direction='row' px={2}>
                    <Grid item xs={4}>
                      <Typography variant='h5' py={1}>
                        1. Select a Function
                      </Typography>
                      <Stack direction='column' spacing={0.5}>
                        {roleOrganizations?.map((org, index) => (
                          <Box
                            borderRadius='4px'
                            display='flex'
                            alignItems='center'
                            justifyContent='center'
                            key={`Role-Org-${org}-${index}`}
                            width='fit-content'
                            color='grey.800'
                            bgcolor='white'
                            border={
                              organization === org ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent'
                            }
                            sx={{
                              ':hover': {
                                bgcolor: theme.palette.grey[50],
                                cursor: 'pointer',
                                border: '1px solid transparent',
                              },
                              px: '5px',
                              py: 0.25,
                            }}
                            onClick={() => dispatch(setRoleOrganization(org))}
                          >
                            <Typography
                              //color={`${theme.palette.grey[800]}`}
                              fontSize='12px'
                              fontWeight={organization === org ? 700 : 600}
                            >
                              {org}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant='h5' py={1}>
                        2. Select a Sub-Function
                      </Typography>
                      {organization && (
                        <Box>
                          <CheckboxFilter
                            title=''
                            choices={functionalCategoriesByOrg}
                            callback={setSelectedRolesState}
                            selected={selectedRolesState}
                            columns={1}
                          />
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant='h5' py={1}>
                        3. Confirm Selections
                      </Typography>
                      <Stack spacing={1}>
                        {selectedRolesState.map((role) => (
                          <Box
                            key={role}
                            sx={{
                              border: `1px solid ${theme.palette.primary.main}`,
                              bgcolor: 'white',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: '4px',
                              '&:hover': {
                                bgcolor: theme.palette.grey[50],
                                border: '1px solid transparent',
                              },
                            }}
                            display='flex'
                            width='fit-content'
                          >
                            <Stack direction='row' spacing={0.5} alignItems='center' justifyContent='center'>
                              <UserFilled width='18px' height='18px' style={{color: 'black'}} />
                              <Typography color={`${theme.palette.grey[800]}`} fontSize='12px' fontWeight={700}>
                                {role}
                              </Typography>
                              <IconButton
                                sx={{p: 0}}
                                disableRipple
                                disableFocusRipple
                                onClick={() => handleDelete(role)}
                              >
                                <Clear fontSize='small' sx={{color: 'grey.800'}} />
                              </IconButton>
                            </Stack>
                          </Box>
                        ))}
                        <Box />
                        <Box />
                        {selectedRolesState.length > 0 && (
                          <Button
                            variant='contained'
                            sx={{
                              width: '90px',
                              height: '40px',
                              bgcolor: theme.palette.primary.main,
                              boxShadow: 0,
                              p: 0,
                            }}
                            onClick={() => {
                              SelectRoles();
                              handleClickAway();
                            }}
                          >
                            Confirm
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </ClickAwayListener>
          </Popper>
        </React.Fragment>
      ) : (
        <Box mr={2}>
          <GuideMe />
        </Box>
      )}
      <Popper
        id={popoverId}
        open={
          userType === LogInType.Internal &&
          onboarding?.roleGuide &&
          !onboarding?.onboardingModal &&
          popoverAnchorEl !== null
        }
        anchorEl={popoverAnchorEl}
        placement='bottom-end'
        sx={{maxWidth: 280, zIndex: 200}}
      >
        <Paper
          sx={{
            bgcolor: theme.palette.grey[200],
            p: '10px',
            pb: '40px',
            mt: 0.5,
            border: `1px solid ${theme.palette.grey[500]}`,
            boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
            borderRadius: '4px',
            backgroundColor: theme.palette.grey[200],
          }}
        >
          <Grid container direction='row' alignItems='center' justifyContent='flex-start' rowSpacing='10px'>
            <Grid item xs={12} display='flex' justifyContent='flex-end' alignItems='flex-start'>
              <IconButton sx={{p: 0}} disableRipple onClick={handleClose}>
                <Clear fontSize='small' sx={{color: 'black'}} />
              </IconButton>
            </Grid>
            <Grid item xs={12} display='flex' alignItems='center' justifyContent='center'>
              <UserFilled width='24px' height='24px' style={{color: theme.palette.grey[800]}} />
            </Grid>
            <Grid item xs={12} display='flex' alignItems='center' justifyContent='center'>
              <Typography variant='h5' fontSize='18px' color='black'>
                Select a function
              </Typography>
            </Grid>

            <Grid item xs={12} display='flex' alignItems='center' justifyContent='center'>
              <Box width='80%' display='flex' alignItems='center' justifyContent='center'>
                <Typography variant='h6' fontSize='14px' textAlign='center'>
                  Click{' '}
                  <Link fontWeight={600} sx={{cursor: 'pointer'}} underline='hover' onClick={() => clickGuideMeLink()}>
                    Guide Me
                  </Link>{' '}
                  then follow the profile icon to content relevant to you
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Popper>
    </Box>
  );
};
export default ChangeRole;
